import supabase from '../supabase/supabase';
import { reportService } from './reportService';

const BUCKETS = ['current', '1-30', '31-60', '61-90', '91+'];
const INCLUDED_STATUSES = ['Open', 'Unpaid', 'Partially Paid', 'Overdue'];

const createBuckets = () => ({
  current: 0,
  '1-30': 0,
  '31-60': 0,
  '61-90': 0,
  '91+': 0
});

const roundCurrency = (value) => Number(Number(value || 0).toFixed(2));

const getAgingBucket = (dueDate, asOfDate) => {
  if (!dueDate) return 'current';

  const due = new Date(`${dueDate}T00:00:00`);
  const asOf = new Date(`${asOfDate}T00:00:00`);
  const daysOverdue = Math.floor((asOf - due) / 86400000);

  if (daysOverdue <= 0) return 'current';
  if (daysOverdue <= 30) return '1-30';
  if (daysOverdue <= 60) return '31-60';
  if (daysOverdue <= 90) return '61-90';
  return '91+';
};

const createCustomer = (id, name) => ({
  id,
  name: name || 'Unassigned customer',
  buckets: createBuckets(),
  invoices: [],
  total: 0
});

const getEmptyReason = (invoiceCount, includedCount, positiveBalanceCount) => {
  if (invoiceCount === 0) return 'No invoices found.';
  if (positiveBalanceCount === 0) return 'All invoices are paid.';
  if (includedCount === 0) return 'No open invoices found.';
  return 'No outstanding receivables found for this date.';
};

export const arAgingService = {
  async generate(asOfDate, accountId) {
    const { account_id } = await reportService.getBaseData(accountId);

    const [{ data: invoices, error: invoiceError }, { data: customers, error: customerError }] = await Promise.all([
      supabase
        .schema('accounting')
        .from('invoices_20240522')
        .select('id, customer_id, invoice_number, issue_date, due_date, amount, status')
        .eq('account_id', account_id)
        .lte('issue_date', asOfDate)
        .in('status', INCLUDED_STATUSES)
        .order('due_date', { ascending: true }),
      supabase
        .schema('accounting')
        .from('customers_20240522')
        .select('id, name')
        .eq('account_id', account_id)
    ]);

    if (invoiceError) throw invoiceError;
    if (customerError) throw customerError;

    const customerNames = new Map(
      (customers || []).map((customer) => [customer.id, customer.name])
    );
    const customersById = new Map();
    let positiveBalanceCount = 0;

    (invoices || []).forEach((invoice) => {
      const outstandingAmount = roundCurrency(invoice.amount);

      if (outstandingAmount > 0) positiveBalanceCount += 1;
      if (outstandingAmount <= 0) return;

      const customerId = invoice.customer_id || `unassigned-${invoice.id}`;
      const customer = customersById.get(customerId) || createCustomer(
        customerId,
        customerNames.get(customerId)
      );
      const dueDate = invoice.due_date || invoice.issue_date;
      const bucket = getAgingBucket(dueDate, asOfDate);

      customer.invoices.push({
        id: invoice.id,
        number: invoice.invoice_number || '—',
        issueDate: invoice.issue_date,
        dueDate,
        balance: outstandingAmount,
        bucket,
        status: invoice.status
      });
      customer.buckets[bucket] = roundCurrency(
        customer.buckets[bucket] + outstandingAmount
      );
      customer.total = roundCurrency(customer.total + outstandingAmount);
      customersById.set(customerId, customer);
    });

    const customerRows = Array.from(customersById.values())
      .map((customer) => ({
        ...customer,
        invoices: customer.invoices.sort((a, b) => (
          String(a.dueDate || '').localeCompare(String(b.dueDate || ''))
        ))
      }))
      .sort((a, b) => b.total - a.total);

    const grandTotals = {
      buckets: createBuckets(),
      total: 0
    };

    customerRows.forEach((customer) => {
      BUCKETS.forEach((bucket) => {
        grandTotals.buckets[bucket] = roundCurrency(
          grandTotals.buckets[bucket] + customer.buckets[bucket]
        );
      });
      grandTotals.total = roundCurrency(grandTotals.total + customer.total);
    });

    const diagnostics = {
      customersFound: (customers || []).length,
      invoicesFound: (invoices || []).length,
      invoicesIncluded: customerRows.reduce(
        (count, customer) => count + customer.invoices.length,
        0
      ),
      invoicesExcluded: Math.max(
        0,
        (invoices || []).length - customerRows.reduce(
          (count, customer) => count + customer.invoices.length,
          0
        )
      ),
      totalOutstandingBalance: grandTotals.total
    };

    console.info('[AR Aging] Database verification', diagnostics);

    return {
      customers: customerRows,
      grandTotals,
      diagnostics,
      emptyReason: customerRows.length > 0
        ? ''
        : getEmptyReason(
          diagnostics.invoicesFound,
          diagnostics.invoicesIncluded,
          positiveBalanceCount
        )
    };
  }
};

export { BUCKETS };